import React from "react";
import { Box, VStack, HStack, Text, Switch } from "@chakra-ui/react";
import { FormattedMessage } from "react-intl";
import BioFlowSubscriptionCard from "src/components/BioFlowSubscriptionCard";
import {
  BIOFLOW_DISPLAY_PRICES,
  BioFlowBillingCycle,
  BioFlowPlanQuotation,
} from "src/types/bioflow";

export type BioFlowQuotationSelection = {
  quotation: BioFlowPlanQuotation;
  billingCycle: BioFlowBillingCycle;
  price: number;
  selected?: string;
  proveUrl?: string;
};

interface BioFlowPlanSelectorProps {
  quotations: BioFlowPlanQuotation[];
  billingCycle: BioFlowBillingCycle;
  onBillingCycleChange: (cycle: BioFlowBillingCycle) => void;
  selection: BioFlowQuotationSelection | null;
  onSelectMethod: (method: string) => void;
  onSubscribe: () => void;
  processing: boolean;
  isActive: boolean;
}

const BioFlowPlanSelector: React.FC<BioFlowPlanSelectorProps> = ({
  quotations,
  billingCycle,
  onBillingCycleChange,
  selection,
  onSelectMethod,
  onSubscribe,
  processing,
  isActive,
}) => {
  const monthlyPlan = quotations.find((q) => q.billingCycle === "monthly");
  const yearlyPlan = quotations.find((q) => q.billingCycle === "yearly");
  const activePlan = billingCycle === "monthly" ? monthlyPlan : yearlyPlan;
  const displayPrice =
    activePlan?.price ??
    (billingCycle === "monthly"
      ? BIOFLOW_DISPLAY_PRICES.monthly
      : BIOFLOW_DISPLAY_PRICES.yearly);

  const toggleBilling = () => {
    onBillingCycleChange(billingCycle === "monthly" ? "yearly" : "monthly");
  };

  return (
    <Box py={6} width="100%" display="flex" flexDirection="column" alignItems="center">
      <VStack spacing={8} align="center" width="100%" maxW="480px">
        <VStack spacing={2}>
          <Text fontSize="3xl" fontWeight="bold" textAlign="center" color="gray.700">
            <FormattedMessage id="bioflow.choosePlan" defaultMessage="SALBioFlow" />
          </Text>
          <Text fontSize="md" color="gray.500" textAlign="center">
            <FormattedMessage
              id="bioflow.choosePlan.subtitle"
              defaultMessage="Bio-Well health scanning and insights"
            />
          </Text>
        </VStack>

        <HStack justify="center" spacing={4}>
          <Text
            fontSize="lg"
            fontWeight={billingCycle === "monthly" ? "bold" : "normal"}
            color={billingCycle === "monthly" ? "brand.900" : "gray.500"}
          >
            <FormattedMessage id="bioflow.monthly" defaultMessage="Monthly" />
          </Text>
          <Switch
            size="lg"
            colorScheme="blue"
            isChecked={billingCycle === "yearly"}
            onChange={toggleBilling}
          />
          <Text
            fontSize="lg"
            fontWeight={billingCycle === "yearly" ? "bold" : "normal"}
            color={billingCycle === "yearly" ? "brand.900" : "gray.500"}
          >
            <FormattedMessage id="bioflow.yearly" defaultMessage="Yearly" />
          </Text>
          {billingCycle === "yearly" && (
            <Text fontSize="sm" color="green.500" fontWeight="semibold">
              (<FormattedMessage id="bioflow.saveWithYearly" defaultMessage="Save $15" />)
            </Text>
          )}
        </HStack>

        <BioFlowSubscriptionCard
          billingCycle={billingCycle}
          price={displayPrice}
          selected={selection?.selected}
          onSelectMethod={onSelectMethod}
          onSubscribe={onSubscribe}
          processing={processing}
          isActive={isActive}
          disabled={!activePlan}
        />
      </VStack>
    </Box>
  );
};

export default BioFlowPlanSelector;
